import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor } from './lib/vdjLook.js';
import { generateMetro, generateMetroBest } from './lib/metro-network.js';

// Contrat du rig partagé : interdit. Le plan est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'metro',
  family: 'graphic',
  danceability: 0.58,
  requiresTier: 0,
  energyLevel: 0.48,
  bpmMin: 90,
  bpmMax: 128,
  movement: 0.50,
  tonality: 0.65,
  enabled: true
};

// METRO : un plan de métro imaginaire qui se dessine sur fond noir. Les
// lignes apparaissent une par une, à 0, 45 et 90°, les stations s'allument,
// des rames lumineuses circulent en rythme ; à la fin du morceau, le réseau
// est complet. Deep house, électro-pop, nu-disco, house minimale.
//
// LE PLAN est généré dans un Web Worker au changement de morceau (R17) par
// lib/metro-network.js (voir son en-tête : grille octolinéaire, A* sur des
// états (nœud, direction, pas droits), correspondances en anneau autour de la
// pochette, arcs de rayon fixe). La graine vient du titre et de l'artiste :
// même morceau, même plan (à cadre égal).
//
// LE RENDU des lignes : UN quad plein écran. Chaque pixel trouve sa cellule
// dans une grille grossière, qui liste les primitives (segments et arcs)
// proches ; il calcule la distance EXACTE à chacune, en pixels d'écran :
// largeur constante de 8 px, bord sur 1,5 px, couleur pleine, une lueur
// exp(-d/6) x 0,22 de la couleur (R1). Le tracé progressif coupe chaque ligne
// à sa longueur dessinée (bout arrondi). Les stations et les rames sont des
// instances ; le halo de chaque instance s'éteint avant le bord de son quad
// (R12, marge de 6 fois la largeur du halo).
//
// LES RAMES : de station en station, CALÉES SUR LES TEMPS : départ sur un
// temps, arrivée sur un temps ultérieur (selon la distance), accélération et
// freinage (smoothstep). Une rame à quai repart au temps suivant : l'arrêt
// dure un temps. Le brief demandait un arrêt d'un demi-temps ET des départs
// et des arrivées sur les temps : les deux ne tiennent pas ensemble, on garde
// les temps. Le corps (24 px) suit la ligne à ABSCISSE CURVILIGNE constante
// (R16) : 7 points échantillonnés sur le tracé exact, jamais d'étirement dans
// un virage ; le fragment calcule la distance à cette polyligne.
//
// LE RYTHME : temps = départs et arrivées ; mesure = une correspondance
// s'illumine en blanc ; phrase = une nouvelle ligne commence (4 mesures de
// tracé) ; drop = toutes les rames à quai partent au même temps, toutes les
// stations flashent. Une rame qui passe une correspondance la fait briller.

const MAX_LINES = 8, MAX_TRAINS = 32, MAX_HUBS = 6, MAX_CELL = 40;
const LINE_W_PX = 8;
const LINE_LUM_MAX = 0.55;
const GLOW_TAU_PX = 6, GLOW_GAIN = 0.22, GLOW_REACH_PX = 40;
const DRAW_BEATS = 16;              // une ligne : 4 mesures
const PHRASE_BEATS = 16;
const TRAIN_LEN_PX = 24;
const TRAIN_SPEED_STEPS = 2.5;      // pas de grille par temps (la durée d'un trajet = distance / vitesse, arrondie au temps)
const STATION_PX = 7.5, HUB_HALF_PX = 9;
// station white under the bloom's threshold: thirty white discs through the
// shared bloom laid a grey veil over the whole black frame
const STATION_WHITE = '0.78';
const NO_BPM = 110;
const SNAP_BEATS = 0.35;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const EXCLUSIONS = [['tr', 0.22, 0.12], ['tl', 0.08, 0.12], ['br', 0.22, 0.10]];
const TIER = { 0: { lines: 5, trains: 2 }, 1: { lines: 8, trains: 3 }, 2: { lines: 8, trains: 4 } };

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
// the seed: the title and the artist (FNV-1a)
export function seedOf(title, artist) {
  const s = String(title || '') + '\u0001' + String(artist || '');
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h >>> 0;
}
export function networkParams(aspect, center, half, screenH, seed, lines) {
  return {
    aspect, screenH, seed, lines, minLines: lines >= 6 ? 6 : lines,
    pitchMin: 0.06, pitchPx: 22, reachPx: STATION_PX + 2, edgeMargin: 0.10,
    safeCenter: center, safeHalf: half, coverMargin: 0.04, exclusions: EXCLUSIONS,
    ds: 0.004, cellSize: 0.1, lineReachPx: GLOW_REACH_PX,
    // R17: about 150 ms on the Pi (2000 expansions/ms on the Mac, the Pi taken 6x slower)
    budgetWork: 50000
  };
}

// --- the worker (R17) ---------------------------------------------------------
let _worker = null, _workerFailed = false, _nextId = 0;
const _waiting = new Map();
function askWorker(msg, cb) {
  if (!_worker && !_workerFailed) {
    try {
      if (typeof Worker === 'undefined') throw new Error('no worker');
      const src = 'var generateMetro = ' + generateMetro.toString() + ';\nvar generateMetroBest = ' + generateMetroBest.toString() + ';\n' +
        'self.onmessage = function (ev) { var t0 = performance.now(); try { var r = generateMetroBest(ev.data.params); r.ms = performance.now() - t0;' +
        ' var tr = [r.prims.buffer, r.primLen.buffer, r.cells.buffer]; for (var i = 0; i < r.samples.length; i++) tr.push(r.samples[i].buffer);' +
        ' self.postMessage({ id: ev.data.id, res: r }, tr); } catch (e) { self.postMessage({ id: ev.data.id, error: String(e && e.message || e) }); } };';
      _worker = new Worker(URL.createObjectURL(new Blob([src], { type: 'text/javascript' })));
      _worker.onmessage = (ev) => { const f = _waiting.get(ev.data.id); if (f) { _waiting.delete(ev.data.id); f(ev.data); } };
      _worker.onerror = () => { for (const [id, f] of _waiting) f({ id, error: 'worker' }); _waiting.clear(); };
    } catch (e) { _workerFailed = true; _worker = null; }
  }
  if (!_worker) return false;
  _waiting.set(msg.id, cb);
  _worker.postMessage(msg);
  return true;
}

// --- colours: distinct, saturated, from the cover --------------------------------
function hueDist(a, b) { const d = Math.abs(a - b) % 1; return Math.min(d, 1 - d); }
// L hues: the cover's own first, then the farthest from all chosen; then each
// line gets the free hue farthest from the lines it MEETS (shared hubs), so
// crossing lines are told apart even when eight hues cannot all be 0.25 apart
export function lineColours(palette, net, L) {
  const hsl = { h: 0, s: 0, l: 0 }, cand = [];
  if (palette) for (const k of ['vividColors', 'dominantColors']) for (const c of (palette[k] || [])) { new THREE.Color(c).getHSL(hsl); if (hsl.s > 0.15) cand.push(hsl.h); }
  const hues = [];
  for (const h of cand) if (hues.length < L && hues.every((x) => hueDist(x, h) >= 0.08)) hues.push(h);
  if (!hues.length) hues.push(0.58);
  while (hues.length < L) {
    let bh = 0, bd = -1;
    for (let k = 0; k < 72; k++) { const h = k / 72, d = Math.min(...hues.map((x) => hueDist(x, h))); if (d > bd) { bd = d; bh = h; } }
    hues.push(bh);
  }
  const meets = Array.from({ length: L }, () => new Set());
  if (net) for (const hub of net.hubs) for (const a of hub.lines) for (const b of hub.lines) if (a !== b && a < L && b < L) meets[a].add(b);
  const assigned = new Array(L).fill(-1), free = hues.slice();
  for (let i = 0; i < L; i++) {
    let best = 0, bestD = -1;
    for (let k = 0; k < free.length; k++) {
      let d = 1;
      for (const j of meets[i]) if (assigned[j] >= 0) d = Math.min(d, hueDist(assigned[j], free[k]));
      if (d > bestD) { bestD = d; best = k; }
    }
    assigned[i] = free.splice(best, 1)[0];
  }
  // luminance capped under the bloom's threshold: a saturated yellow (0.93)
  // bloomed into a grey veil over the whole frame
  return assigned.map((h) => {
    const c = vdjColor(new THREE.Color().setHSL(h, 1, 0.5).getHex(), new THREE.Color(), 0.9);
    const lum = 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
    return lum > LINE_LUM_MAX ? c.multiplyScalar(LINE_LUM_MAX / lum) : c;
  });
}

// --- GLSL ------------------------------------------------------------------------
const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// The lines: per pixel, the primitives of its cell; exact distance, cut at
// each line's drawn length (round end), drawn in line order (later on top).
const linesFragmentShader = `
  #ifdef GL_FRAGMENT_PRECISION_HIGH
  precision highp float;
  #else
  precision mediump float;
  #endif
  const float TAU = 6.2831853;
  uniform sampler2D uPrims;   // 2 x count: (a, b) or (c, r, a0); (sweep, s0, line, type)
  uniform sampler2D uCells;   // cellW x cells: prim index, -1 at the end
  uniform vec2 uPrimTex;      // 2, count
  uniform vec2 uCellTex;      // cellW, cells
  uniform vec2 uGrid;         // CX, CY
  uniform float uCS;
  uniform float uAspect;
  uniform float uPxPerUnit;
  uniform sampler2D uLineTex; // ${MAX_LINES} x 1: colour, drawn length
  varying vec2 vUv;
  void main() {
    vec2 p = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 cell = floor((p + vec2(uAspect, 1.0)) / uCS);
    cell = clamp(cell, vec2(0.0), uGrid - 1.0);
    float row = (cell.y * uGrid.x + cell.x + 0.5) / uCellTex.y;
    vec3 col = vec3(0.0), glow = vec3(0.0);
    float cover = 0.0;
    for (int i = 0; i < ${MAX_CELL}; i++) {
      if (float(i) >= uCellTex.x) break;
      float pi = texture2D(uCells, vec2((float(i) + 0.5) / uCellTex.x, row)).r;
      if (pi < 0.0) break;
      float v = (pi + 0.5) / uPrimTex.y;
      vec4 t0 = texture2D(uPrims, vec2(0.25, v));
      vec4 t1 = texture2D(uPrims, vec2(0.75, v));
      float line = t1.z, s0 = t1.y;
      vec4 ld = texture2D(uLineTex, vec2((line + 0.5) / ${MAX_LINES}.0, 0.5));
      float drawn = ld.a;
      if (drawn <= s0) continue;
      float d;
      if (t1.w < 0.5) {
        vec2 a = t0.xy, b = t0.zw, ab = b - a;
        float L = length(ab);
        float hmax = clamp((drawn - s0) / max(L, 1e-6), 0.0, 1.0);
        float h = clamp(dot(p - a, ab) / max(L * L, 1e-9), 0.0, hmax);
        d = length(p - a - ab * h);
      } else {
        vec2 c = t0.xy; float r = t0.z, a0 = t0.w, sw = t1.x;
        float sg = sw > 0.0 ? 1.0 : -1.0;
        float amax = min(abs(sw), (drawn - s0) / r);
        vec2 q = p - c;
        float rel = mod((atan(q.y, q.x) - a0) * sg, TAU);
        // inside the drawn arc: radial distance; beyond: the nearer end
        if (rel <= amax) d = abs(length(q) - r);
        else {
          vec2 e0 = c + r * vec2(cos(a0), sin(a0)), e1 = c + r * vec2(cos(a0 + sg * amax), sin(a0 + sg * amax));
          d = min(length(p - e0), length(p - e1));
        }
      }
      float dpx = d * uPxPerUnit;
      float hw = ${(LINE_W_PX / 2).toFixed(1)};
      float a = 1.0 - smoothstep(hw - 0.75, hw + 0.75, dpx);
      vec3 lc = ld.rgb;
      col = mix(col, lc, a);
      cover = max(cover, a);
      glow = max(glow, lc * ${GLOW_GAIN.toFixed(2)} * exp(-max(dpx - hw, 0.0) / ${GLOW_TAU_PX.toFixed(1)}));
    }
    gl_FragColor = vec4(col + glow * (1.0 - cover), 1.0);
  }
`;

// Stations: a white disc ringed in the line's colour; interchanges: a white
// pill ringed in black. Visible once the line's drawing reaches them, with a
// flash; interchanges also glow (a train passing, the bar, the drop).
const stationVertexShader = `
  precision highp float;
  attribute vec4 iA;         // x, y, line, s
  attribute vec4 iB;         // kind (0 stop, 1 interchange, 2 terminus), hub, axis angle, -
  uniform float uAspect;
  uniform float uPxPerUnit;
  uniform float uElapsed[${MAX_LINES}];  // beats since the line began (-1: not begun)
  uniform float uLineLen[${MAX_LINES}];
  uniform float uBeatS;
  varying vec2 vLoc;         // px
  varying float vKind;
  varying float vFlash;
  varying float vHub;
  varying float vLine;
  varying float vAxis;
  void main() {
    float el = uElapsed[0], len = uLineLen[0];
    for (int k = 1; k < ${MAX_LINES}; k++) if (abs(iA.z - float(k)) < 0.5) { el = uElapsed[k]; len = uLineLen[k]; }
    // the drawing reaches s at s / len of its ${DRAW_BEATS} beats
    float sinceBeats = el - iA.w / max(len, 1e-4) * ${DRAW_BEATS}.0;
    if (el < 0.0 || sinceBeats < -1e-3) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float since = max(sinceBeats, 0.0) * uBeatS;                // seconds since the drawing reached it
    vFlash = exp(-since / 0.35);
    vKind = iB.x; vHub = iB.y; vLine = iA.z; vAxis = iB.z;
    float R = 40.0;                                          // the halo dies before the quad's edge (R12)
    vLoc = position.xy * R;
    vec2 p = iA.xy * uPxPerUnit + vLoc;
    gl_Position = vec4(p.x / uPxPerUnit / uAspect, p.y / uPxPerUnit, 0.0, 1.0);
  }
`;
const stationFragmentShader = `
  precision highp float;
  uniform vec3 uColor[${MAX_LINES}];
  uniform float uHubGlow[${MAX_HUBS}];
  uniform float uFlashAll;
  varying vec2 vLoc;
  varying float vKind;
  varying float vFlash;
  varying float vHub;
  varying float vLine;
  varying float vAxis;
  vec3 lineColour(float l) {
    vec3 c = uColor[0];
    for (int k = 1; k < ${MAX_LINES}; k++) if (abs(l - float(k)) < 0.5) c = uColor[k];
    return c;
  }
  float hubGlow(float h) {
    float g = uHubGlow[0];
    for (int k = 1; k < ${MAX_HUBS}; k++) if (abs(h - float(k)) < 0.5) g = uHubGlow[k];
    return g;
  }
  void main() {
    vec3 c = vec3(0.0); float a = 0.0;
    float glowAmt = vFlash + uFlashAll;
    float d;                                  // signed distance to the outer edge, px
    if (vKind > 0.5 && vKind < 1.5) {
      // interchange: a capsule along its axis, white, a black 2 px ring
      float ca = cos(vAxis), sa = sin(vAxis);
      vec2 q = vec2(ca * vLoc.x + sa * vLoc.y, -sa * vLoc.x + ca * vLoc.y);
      float hl = ${(HUB_HALF_PX - 6).toFixed(1)};
      vec2 e = vec2(max(abs(q.x) - hl, 0.0), q.y);
      d = length(e) - 6.0;
      float inner = 1.0 - smoothstep(-2.75, -1.25, d);
      float outer = 1.0 - smoothstep(-0.75, 0.75, d);
      c = mix(vec3(0.0), vec3(${STATION_WHITE}), inner);
      a = outer;
      glowAmt += hubGlow(vHub);
    } else {
      // stop / terminus: a white disc, a 2.5 px ring of the line's colour
      float r = length(vLoc), R = ${STATION_PX.toFixed(1)} + (vKind > 1.5 ? 1.5 : 0.0);
      d = r - R;
      float inner = 1.0 - smoothstep(-3.25, -1.75, d);
      float outer = 1.0 - smoothstep(-0.75, 0.75, d);
      c = mix(lineColour(vLine), vec3(${STATION_WHITE}), inner);
      a = outer;
    }
    // the flash / glow: a white bloom around the station, gone at 6 x 4 px
    float halo = glowAmt * 0.55 * exp(-max(d, 0.0) / 4.0) * (1.0 - smoothstep(18.0, 24.0, max(d, 0.0)));
    vec3 rgb = c * a + vec3(1.0, 0.97, 0.9) * halo * (1.0 - a) + vec3(0.25) * glowAmt * a * 0.5;
    gl_FragColor = vec4(rgb, max(a, halo));
  }
`;

// Trains: one quad round the 7-point body, the fragment's distance to that
// polyline: a white core and a halo of the line's colour (R1), gone before
// the quad's edge (R12).
const trainVertexShader = `
  precision highp float;
  attribute float iTrain;
  uniform float uAspect;
  uniform float uPxPerUnit;
  uniform vec4 uPts[${MAX_TRAINS * 4}];    // 7 points per train, 2 per vec4 (the 8th unused)
  uniform float uOn[${MAX_TRAINS}];
  uniform vec3 uTrainColor[${MAX_TRAINS}];
  varying vec2 vP;
  varying vec4 vA; varying vec4 vB; varying vec4 vC; varying vec2 vD;
  varying vec3 vCol;
  void main() {
    int t = int(iTrain + 0.5);
    float on = uOn[t];
    vCol = uTrainColor[t];
    if (on < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec4 a = uPts[t * 4], b = uPts[t * 4 + 1], c = uPts[t * 4 + 2], d = uPts[t * 4 + 3];
    vec2 lo = min(min(min(a.xy, a.zw), min(b.xy, b.zw)), min(min(c.xy, c.zw), d.xy));
    vec2 hi = max(max(max(a.xy, a.zw), max(b.xy, b.zw)), max(max(c.xy, c.zw), d.xy));
    float m = 22.0 / uPxPerUnit;
    vec2 corner = position.xy * 0.5 + 0.5;
    vec2 p = mix(lo - m, hi + m, corner);
    vP = p; vA = a; vB = b; vC = c; vD = d.xy;
    gl_Position = vec4(p.x / uAspect, p.y, 0.0, 1.0);
  }
`;
const trainFragmentShader = `
  precision highp float;
  uniform float uPxPerUnit;
  varying vec2 vP;
  varying vec4 vA; varying vec4 vB; varying vec4 vC; varying vec2 vD;
  varying vec3 vCol;
  float seg(vec2 p, vec2 a, vec2 b) { vec2 ab = b - a; float h = clamp(dot(p - a, ab) / max(dot(ab, ab), 1e-12), 0.0, 1.0); return length(p - a - ab * h); }
  void main() {
    float d = seg(vP, vA.xy, vA.zw);
    d = min(d, seg(vP, vA.zw, vB.xy));
    d = min(d, seg(vP, vB.xy, vB.zw));
    d = min(d, seg(vP, vB.zw, vC.xy));
    d = min(d, seg(vP, vC.xy, vC.zw));
    d = min(d, seg(vP, vC.zw, vD));
    float dpx = d * uPxPerUnit;
    vec3 lc = vCol;
    float core = 1.0 - smoothstep(1.75, 3.25, dpx);
    float halo = 0.75 * exp(-max(dpx - 2.5, 0.0) / 3.0) * (1.0 - smoothstep(16.0, 20.0, dpx));
    vec3 rgb = vec3(0.86, 0.84, 0.80) * core + lc * halo * (1.0 - core);
    if (core + halo < 1e-3) discard;
    // premultiplied OVER, not additive: added onto a line, the halo pushed it
    // past the bloom's threshold and the frame went grey
    gl_FragColor = vec4(rgb, core + halo * (1.0 - core));
  }
`;

export class MetroVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 1;
    this._L = TIER[this._tier];
    const s = parseInt(urlParam('metroseed'), 10);
    this._forceSeed = Number.isFinite(s) ? s >>> 0 : null;
    this._complete = urlParam('metrocomplete') === '1';
    this._forceDrop = urlParam('metrodrop') === '1';
    this._layers = (urlParam('metrolayers') || 'lines,stations,trains').split(',');
    this._post = { bloomStrength: 0.25, bloomThreshold: 0.86, grain: 0, vignette: 0.25, aberration: 0.0008, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    if (urlParam('metroprobe') === '1' && typeof window !== 'undefined') window.__metro = this;
  }

  get postProcessing() { return this._post; }

  // the probe's log (?metroprobe=1), bounded: a kiosk runs for days
  _note(e) { this._log.push(e); if (this._log.length > 40) this._log.shift(); }

  init(scene) {
    this._scene = scene;
    const colours = () => Array.from({ length: MAX_LINES }, () => new THREE.Color(0.5, 0.5, 0.5));
    this._shared = {
      uAspect: { value: 1.78 }, uPxPerUnit: { value: 540 },
      uDrawn: { value: new Array(MAX_LINES).fill(-1) }, uColor: { value: colours() }
    };
    const S = this._shared;
    this._lineData = new Float32Array(MAX_LINES * 4);
    const lt = new THREE.DataTexture(this._lineData, MAX_LINES, 1, THREE.RGBAFormat, THREE.FloatType);
    lt.minFilter = lt.magFilter = THREE.NearestFilter; lt.needsUpdate = true;
    this._lineU = Object.assign({}, S, {
      uLineTex: { value: lt },
      uPrims: { value: null }, uCells: { value: null }, uPrimTex: { value: new THREE.Vector2(2, 1) }, uCellTex: { value: new THREE.Vector2(1, 1) },
      uGrid: { value: new THREE.Vector2(1, 1) }, uCS: { value: 0.1 }
    });
    this._lines = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._lineU, vertexShader: quadVertexShader, fragmentShader: linesFragmentShader, depthTest: false, depthWrite: false
    }));
    this._lines.frustumCulled = false; this._lines.renderOrder = -1000; this._lines.visible = false;
    const self = this;
    this._lines.onBeforeRender = function (renderer) { self._layout(renderer); };
    scene.add(this._lines);
    // the layout hook must run even before the first network: a tiny always-drawn mesh
    this._probe = new THREE.Mesh(new THREE.PlaneGeometry(0.0001, 0.0001), new THREE.MeshBasicMaterial({ color: 0x000000 }));
    this._probe.frustumCulled = false; this._probe.renderOrder = -1001;
    this._probe.onBeforeRender = function (renderer) { self._layout(renderer); };
    scene.add(this._probe);

    this._stationU = Object.assign({}, S, {
      uElapsed: { value: new Array(MAX_LINES).fill(-1) }, uLineLen: { value: new Array(MAX_LINES).fill(1) }, uBeatS: { value: 0.5 }, uHubGlow: { value: new Array(MAX_HUBS).fill(0) }, uFlashAll: { value: 0 } });
    this._stations = null;
    this._trainU = Object.assign({}, S, {
      uPts: { value: Array.from({ length: MAX_TRAINS * 4 }, () => new THREE.Vector4()) },
      uOn: { value: new Array(MAX_TRAINS).fill(0) }, uTrainColor: { value: Array.from({ length: MAX_TRAINS }, () => new THREE.Color()) }
    });
    const tg = new THREE.InstancedBufferGeometry();
    const base = new THREE.PlaneGeometry(2, 2);
    tg.setAttribute('position', base.getAttribute('position')); tg.setIndex(base.getIndex());
    const ti = new Float32Array(MAX_TRAINS); for (let i = 0; i < MAX_TRAINS; i++) ti[i] = i;
    tg.setAttribute('iTrain', new THREE.InstancedBufferAttribute(ti, 1));
    tg.instanceCount = MAX_TRAINS;
    this._trains = new THREE.Mesh(tg, new THREE.ShaderMaterial({
      uniforms: this._trainU, vertexShader: trainVertexShader, fragmentShader: trainFragmentShader,
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor
    }));
    this._trains.frustumCulled = false; this._trains.renderOrder = -998; this._trains.visible = false;
    scene.add(this._trains);

    // state
    this._time = 0; this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM;
    this._net = null; this._netKey = ''; this._job = 0; this._screenH = 0;
    this._trackSeen = undefined; this._paletteKey = '';
    this._lineStart = []; this._lastStart = -1e9;
    this._trainsState = [];
    this._hubFlash = new Array(MAX_HUBS).fill(0); this._barHub = 0; this._flashAll = 0;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._log = [];
    this._profile = null;
  }

  // the screen's size (the reaches are in px), and the network's request
  _layout(renderer) {
    const rt = renderer.getRenderTarget();
    const h = rt ? rt.height : renderer.getDrawingBufferSize(_size).y;
    this._shared.uPxPerUnit.value = h / 2;
    if (Math.abs(h - this._screenH) > 0.5) { this._screenH = h; this._requestNetwork(); }
  }

  _requestNetwork() {
    const p = this._profile;
    if (!p || !this._screenH) return;
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    // R19: the network avoids the stage centre's block, not the cover's position
    const c = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value;
    const seed = this._forceSeed != null ? this._forceSeed : seedOf(p.title, p.artist);
    const key = [seed, aspect.toFixed(2), c.x.toFixed(2), c.y.toFixed(2), hs.x.toFixed(2), hs.y.toFixed(2), Math.round(this._screenH / 40)].join(',');
    if (key === this._netKey) return;
    this._netKey = key;
    const params = networkParams(aspect, [c.x, c.y], [hs.x, hs.y], this._screenH, seed, this._L.lines);
    this._note({ what: 'ask', key });
    const job = ++this._job, id = ++_nextId;
    const done = (res) => { if (job === this._job && this._scene) this._install(res, aspect); };
    const sent = askWorker({ id, params }, (m) => {
      if (m.error || !m.res) { this._note({ what: 'net', error: m.error }); done(generateMetroBest(params)); return; }
      this._note({ what: 'net', ms: Math.round(m.res.ms), lines: m.res.lines.length, inter: m.res.interchanges });
      done(m.res);
    });
    if (!sent) done(generateMetroBest(params));      // no worker at all: the main thread, once
  }

  _install(net, aspect) {
    const first = !this._net;
    this._net = net;
    const U = this._lineU, S = this._shared;
    S.uAspect.value = aspect;
    if (U.uPrims.value) U.uPrims.value.dispose();
    if (U.uCells.value) U.uCells.value.dispose();
    const pt = new THREE.DataTexture(net.prims, 2, net.primCount, THREE.RGBAFormat, THREE.FloatType);
    pt.minFilter = pt.magFilter = THREE.NearestFilter; pt.needsUpdate = true;
    const cellRows = net.CX * net.CY;
    const cdata = new Float32Array(net.cellW * cellRows * 4).fill(-1);
    for (let i = 0; i < net.cellW * cellRows; i++) cdata[i * 4] = net.cells[i];
    const ct = new THREE.DataTexture(cdata, net.cellW, cellRows, THREE.RGBAFormat, THREE.FloatType);
    ct.minFilter = ct.magFilter = THREE.NearestFilter; ct.needsUpdate = true;
    U.uPrims.value = pt; U.uCells.value = ct;
    U.uPrimTex.value.set(2, net.primCount); U.uCellTex.value.set(net.cellW, cellRows);
    U.uGrid.value.set(net.CX, net.CY); U.uCS.value = net.CS;
    this._lines.visible = true;

    // stations, by line then s; interchanges once (at the first line that reaches them)
    if (this._stations) { this._scene.remove(this._stations); this._stations.geometry.dispose(); this._stations.material.dispose(); }
    const list = [], hubDone = new Set();
    const sorted = net.stations.slice().sort((a, b) => a.line - b.line || a.s - b.s);
    for (const st of sorted) {
      if (st.kind === 1) { if (hubDone.has(st.hub)) continue; hubDone.add(st.hub); }
      list.push(st);
    }
    const g = new THREE.InstancedBufferGeometry();
    const base = new THREE.PlaneGeometry(2, 2);
    g.setAttribute('position', base.getAttribute('position')); g.setIndex(base.getIndex());
    const ia = new Float32Array(list.length * 4), ib = new Float32Array(list.length * 4);
    list.forEach((st, i) => {
      ia.set([st.x, st.y, st.line, st.s], i * 4);
      // an interchange's pill lies along the bisector of its lines' directions
      let axis = 0;
      if (st.kind === 1) axis = this._hubAxis(net, st.hub);
      ib.set([st.kind, st.hub, axis, 0], i * 4);
    });
    g.setAttribute('iA', new THREE.InstancedBufferAttribute(ia, 4));
    g.setAttribute('iB', new THREE.InstancedBufferAttribute(ib, 4));
    g.instanceCount = list.length;
    this._stations = new THREE.Mesh(g, new THREE.ShaderMaterial({
      uniforms: this._stationU, vertexShader: stationVertexShader, fragmentShader: stationFragmentShader,
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor
    }));
    this._stations.frustumCulled = false; this._stations.renderOrder = -999;
    this._scene.add(this._stations);
    this._trains.visible = true;

    // line stops (for the trains): every station of each line, sorted by s
    this._stops = net.lines.map((l, i) => net.stations.filter((st) => st.line === i).map((st) => ({ s: st.s, hub: st.kind === 1 ? st.hub : -1 })).sort((a, b) => a.s - b.s));
    this._applyColours();
    // a new plan mid-track (the frame changed) keeps the drawing's progress;
    // its complete lines put their trains back in service on the next update
    if (first || this._complete) { this._lineStart = []; this._lastStart = -1e9; }
    this._trainsState = [];
    this._shared.uDrawn.value.fill(-1);
    this._note({ what: 'install', lines: net.lines.length, prims: net.primCount, stations: list.length });
  }

  _hubAxis(net, hub) {
    // the direction of the hub's lines from the samples near it
    const h = net.hubs[hub];
    let sx = 0, sy = 0;
    for (const li of h.lines) {
      const smp = net.samples[li];
      let best = 1e9, bi = 0;
      for (let k = 0; k < smp.length / 2; k++) { const d = (smp[k * 2] - h.x) ** 2 + (smp[k * 2 + 1] - h.y) ** 2; if (d < best) { best = d; bi = k; } }
      const k2 = Math.min(smp.length / 2 - 1, bi + 3), k1 = Math.max(0, bi - 3);
      let dx = smp[k2 * 2] - smp[k1 * 2], dy = smp[k2 * 2 + 1] - smp[k1 * 2 + 1];
      const L = Math.hypot(dx, dy) || 1; dx /= L; dy /= L;
      // directions are axial: fold onto the half plane before summing (doubled angle)
      const a = Math.atan2(dy, dx) * 2; sx += Math.cos(a); sy += Math.sin(a);
    }
    // the pill lies ACROSS the lines: their mean direction + 90 degrees
    return Math.atan2(sy, sx) / 2 + Math.PI / 2;
  }

  _applyColours() {
    if (!this._net) return;
    const cols = lineColours(this._palette, this._net, this._net.lines.length);
    for (let i = 0; i < MAX_LINES; i++) this._shared.uColor.value[i].copy(cols[i % cols.length]);
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  // --- the trains --------------------------------------------------------------
  _spawnTrains(line) {
    const stops = this._stops[line];
    if (!stops || stops.length < 2) return;
    const K = Math.min(this._L.trains, stops.length - 1);
    for (let k = 0; k < K && this._trainsState.length < MAX_TRAINS; k++) {
      const idx = Math.round(k * (stops.length - 1) / Math.max(1, K - 1 || 1)) % stops.length;
      const dir = k % 2 === 0 ? 1 : -1;
      this._trainsState.push({ line, idx: idx === stops.length - 1 && dir > 0 ? idx - 1 : idx, dir, moving: false, s: stops[idx].s,
        nextDepart: Math.ceil(this._clock) + k, from: 0, to: 0, depart: 0, arrive: 0 });
    }
  }
  _tickTrains() {
    const P = this._net.P, v = TRAIN_SPEED_STEPS * P;
    for (const T of this._trainsState) {
      const stops = this._stops[T.line];
      if (T.moving && this._clock >= T.arrive) {
        T.moving = false; T.s = T.to; T.idx = T.toIdx;
        T.nextDepart = T.arrive + 1;                 // at the quay until the next beat
      }
      if (!T.moving && this._clock >= T.nextDepart) {
        let next = T.idx + T.dir;
        if (next < 0 || next >= stops.length) { T.dir = -T.dir; next = T.idx + T.dir; }
        const dist = Math.abs(stops[next].s - stops[T.idx].s);
        const n = Math.max(1, Math.round(dist / v));
        T.from = stops[T.idx].s; T.to = stops[next].s; T.toIdx = next;
        T.depart = T.nextDepart; T.arrive = T.depart + n; T.moving = true;
      }
      if (T.moving) T.s = T.from + (T.to - T.from) * smooth((this._clock - T.depart) / (T.arrive - T.depart));
    }
  }
  _pointAt(line, s, out) {
    const smp = this._net.samples[line], ds = this._net.ds, n = smp.length / 2;
    const f = Math.max(0, Math.min(n - 1, s / ds)), i = Math.min(n - 2, Math.floor(f)), u = f - i;
    out[0] = smp[i * 2] + (smp[i * 2 + 2] - smp[i * 2]) * u;
    out[1] = smp[i * 2 + 1] + (smp[i * 2 + 3] - smp[i * 2 + 1]) * u;
    return out;
  }
  _pushTrains() {
    const U = this._trainU, px = this._shared.uPxPerUnit.value, len = TRAIN_LEN_PX / px;
    const p = [0, 0];
    for (let t = 0; t < MAX_TRAINS; t++) {
      const T = this._trainsState[t];
      U.uOn.value[t] = T ? 1 : 0;
      if (!T) continue;
      const L = this._net.lines[T.line].len;
      // the body BEHIND the head along the direction of travel, at constant arc length
      const back = T.moving ? Math.sign(T.to - T.from) : T.dir;
      const pts = [];
      for (let k = 0; k < 7; k++) {
        const sk = Math.max(0, Math.min(L, T.s - back * len * (k / 6 - 0.5)));
        pts.push(this._pointAt(T.line, sk, p).slice());
      }
      for (let q = 0; q < 4; q++) {
        const a = pts[q * 2], b = pts[Math.min(6, q * 2 + 1)];
        U.uPts.value[t * 4 + q].set(a[0], a[1], b[0], b[1]);
      }
      U.uTrainColor.value[t].copy(this._shared.uColor.value[T.line]);
    }
  }

  update(deltaTime, profile) {
    if (this._net) {
      this._lines.visible = this._layers.includes('lines');
      this._trains.visible = this._layers.includes('trains');
      if (this._stations) this._stations.visible = this._layers.includes('stations');
    }
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._profile = profile;
    if (profile.trackId !== this._trackSeen) {
      const first = this._trackSeen === undefined;
      this._trackSeen = profile.trackId;
      this._netKey = ''; if (!first) { this._net = null; this._lines.visible = false; this._trains.visible = false; if (this._stations) this._stations.visible = false; }
      this._lineStart = []; this._lastStart = -1e9; this._trainsState = [];
    }
    const pal = profile.artworkPalette;
    const pk = pal ? String((pal.vividColors || []).slice(0, 4)) : '';
    if (pk !== this._paletteKey) { this._paletteKey = pk; this._palette = pal; this._applyColours(); }
    this._requestNetwork();
    if (profile.playbackState !== 'PLAYING') return;        // frozen
    this._time += dt;

    // the clock, in beats, pulled to the music's bars
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase, ph = profile.phrasePhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? bpm : NO_BPM;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      // a jump (seek, drift) snaps: trains leave ON the beat, a clock chasing
      // a 1-beat error for two seconds put every departure off it
      if (Math.abs(err) > SNAP_BEATS) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else { const rate = this._bpm / 60; this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5)); }
    } else { this._clock += dt * this._bpm / 60; this._targetOffset = null; }
    let phraseEdge = false;
    if (hasGrid && ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phraseEdge = true; this._phasePhrase = ph; }
    const barEdge = Math.floor(this._clockPrev / 4) !== Math.floor(this._clock / 4);
    if (!(hasGrid && ph != null) && Math.floor(this._clockPrev / PHRASE_BEATS) !== Math.floor(this._clock / PHRASE_BEATS)) phraseEdge = true;

    if (!this._net) return;
    const net = this._net, L = net.lines.length;
    const beatS = 60 / this._bpm;
    // THE DRAWING: a new line on each phrase; catching up so the network is
    // complete by the end of the track (by 85 % of it)
    if (this._complete) { for (let i = 0; i < L; i++) if (this._lineStart[i] == null) this._lineStart[i] = this._clock - DRAW_BEATS - 8; }
    else {
      const started = this._lineStart.length;
      const pos = profile.estimatedPosition != null ? profile.estimatedPosition : profile.position;
      const progress = profile.duration > 0 && Number.isFinite(pos) ? pos / profile.duration : null;
      const behind = progress != null && started < L && started < Math.ceil(L * Math.min(1, progress / 0.85));
      // a phrase grid that never wraps (a looping position) still gets a new
      // line every two phrases' worth of beats, on a bar
      const overdue = barEdge && this._clock - this._lastStart >= 2 * PHRASE_BEATS;
      if (started === 0 || (started < L && (phraseEdge || overdue || (behind && barEdge && this._clock - this._lastStart >= 4)))) {
        this._lineStart.push(started === 0 ? 4 * Math.ceil(this._clock / 4) : (phraseEdge ? this._clock : 4 * Math.round(this._clock / 4)));
        this._lastStart = this._clock;
        this._note({ what: 'line', i: started, beat: +this._clock.toFixed(2) });
      }
    }
    for (let i = 0; i < MAX_LINES; i++) {
      const st = this._lineStart[i];
      const len = i < L ? net.lines[i].len : 0;
      const prev = this._shared.uDrawn.value[i];
      // -1: not started (its first station, at s = 0, stays hidden)
      const d = st == null || this._clock < st ? -1 : len * clamp01((this._clock - st) / DRAW_BEATS);
      this._shared.uDrawn.value[i] = d;
      // a line complete: its trains come into service
      if (i < L && prev < len && d >= len - 1e-6) this._spawnTrains(i);
    }
    const ldat = this._lineData;
    for (let i = 0; i < MAX_LINES; i++) { const c = this._shared.uColor.value[i]; ldat[i * 4] = c.r; ldat[i * 4 + 1] = c.g; ldat[i * 4 + 2] = c.b; ldat[i * 4 + 3] = this._shared.uDrawn.value[i]; }
    this._lineU.uLineTex.value.needsUpdate = true;
    if (this._complete && this._trainsState.length === 0) for (let i = 0; i < L; i++) this._spawnTrains(i);
    const SU = this._stationU;
    SU.uBeatS.value = beatS;
    for (let i = 0; i < MAX_LINES; i++) {
      const st = this._lineStart[i];
      SU.uElapsed.value[i] = i < L && st != null && this._clock >= st ? this._clock - st : -1;
      SU.uLineLen.value[i] = i < L ? net.lines[i].len : 1;
    }

    // the drop: every train at a quay leaves on the same beat; every station flashes
    let drop = hasGrid && this._tickDrops(profile);
    if (this._forceDrop && barEdge && Math.floor(this._clock / 4) % 2 === 1) drop = true;   // ?metrodrop=1: every other bar
    if (drop) {
      const b = Math.ceil(this._clock);
      for (const T of this._trainsState) if (!T.moving) T.nextDepart = b;
      this._flashAll = 1;
      this._note({ what: 'drop', beat: +this._clock.toFixed(2) });
    }
    this._flashAll *= Math.exp(-dt / 0.35);
    this._stationU.uFlashAll.value = this._flashAll;

    this._tickTrains();
    this._pushTrains();

    // interchanges: the bar's (one after another), and those a train is passing
    if (barEdge && net.hubs.length) { this._barHub = (this._barHub + 1) % net.hubs.length; this._hubFlash[this._barHub] = 1; }
    const lenU = TRAIN_LEN_PX / this._shared.uPxPerUnit.value;
    for (let h = 0; h < MAX_HUBS; h++) {
      let g = this._hubFlash[h] *= Math.exp(-dt / 0.45);
      for (const T of this._trainsState) {
        const st = this._stops[T.line].find((x) => x.hub === h);
        if (st && Math.abs(T.s - st.s) < lenU) g = Math.max(g, 0.8 * (1 - Math.abs(T.s - st.s) / lenU));
      }
      this._stationU.uHubGlow.value[h] = g;
    }
  }

  dispose() {
    for (const m of [this._lines, this._probe, this._stations, this._trains]) {
      if (!m) continue;
      m.onBeforeRender = null;
      if (this._scene) this._scene.remove(m);
      m.geometry.dispose(); m.material.dispose();
    }
    const U = this._lineU;
    if (U) { U.uLineTex.value.dispose(); if (U.uPrims.value) U.uPrims.value.dispose(); if (U.uCells.value) U.uCells.value.dispose(); }
    if (typeof window !== 'undefined' && window.__metro === this) window.__metro = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
